/**
 * QR Code PDF Renderer for @react-pdf/renderer
 *
 * Draws a QR matrix as a grid of module cells (Views) — no raster/Image
 * dependency, purely vector, so it renders crisply at any PDF zoom level.
 */

import React from 'react';
import { View } from '@react-pdf/renderer';
import { generateQRMatrix } from '../../utils/electrical/qrcode.js';

/**
 * QRCodeView - Draw a QR code inside a PDF document.
 * @param {Object} props
 * @param {string|Object} props.payload - QR text or object (JSON-stringified)
 * @param {number} props.moduleSize - Pixel size of each dark module (default 3)
 * @param {string} props.color - Module fill color (default '#000000')
 * @param {number} props.quietZone - Quiet-zone padding in modules (default 4)
 * @param {Object} props.style - Additional style wrapper (positioning, margin)
 */
const QRCodeView = ({ payload, moduleSize = 3, color = '#000000', quietZone = 4, style }) => {
  const matrix = generateQRMatrix(payload);
  const size = matrix.length;
  const withQuiet = size + quietZone * 2;
  const pixel = withQuiet * moduleSize;

  // Build a flat list of dark cells to minimise the number of rendered Views.
  const darkCells = [];
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (matrix[row][col]) {
        darkCells.push({
          left: (col + quietZone) * moduleSize,
          top: (row + quietZone) * moduleSize
        });
      }
    }
  }

  return (
    <View style={[{
      width: pixel,
      height: pixel,
      position: 'relative',
      backgroundColor: '#FFFFFF',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: '#000000'
    }, style]}>
      {darkCells.map((cell, i) => (
        <View
          key={i}
          style={{
            position: 'absolute',
            left: cell.left,
            top: cell.top,
            width: moduleSize,
            height: moduleSize,
            backgroundColor: color
          }}
        />
      ))}
    </View>
  );
};

export default QRCodeView;